<template>
    <wrapper-features-templates-operations
    :hideContentWrapper="!renderStep1 && !showLoader && !applyErrorState"
    :showLoader="!renderStep1 && !applyErrorState ? showLoader : null"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :feedbackOperationData="headerOperationData"
    :showStepper="!applyErrorState && !isValidated && renderStep1"
    :activeStep="activeStep"
    :stepperParams="stepperParams"
    :showFooter="renderStep1 && !isValidated && selectedOperations.length && !showLoader"
    :footerButtonsParams="footerButtonsParams"
    :footerWithInfoDisplayer="activeStep === 1"
    :scrollValue="scrollValue"
    :tokenTitleAppend="t('approvals.pendingSignature.modal.tokenModal.title')"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event, requestSignOperations)"
    @backOrCloseClicked="emit('backOrCloseClicked')"
    @clearScrollToValue="scrollValue = null">
        <!-- Content -->
        <transition-group name="common-enter-animation" mode="out-in" appear>
            <div
            v-if="renderStep1 && !applyErrorState"
            :key="activeStep"
            class="content-wrapper"
            :class="{'in-step-3': activeStep === 3}">
                <!-- Steps Components Content -->
                <component
                :is="activeStepComponent"
                :accountsDropdownOptions="getAccountsWithOperationsPendingSignature"
                :selectedAccount="selectedAccount"
                :operationsList="getOperationApprovalsList"
                :hasMore="getApprovalsHasMore"
                :isLoadingMore="tableLoadingMore"
                :selectedOperations="selectedOperations"
                :successfulOperationApprovalsList="successfulOperationApprovalsList"
                :failedOperationApprovalsList="failedOperationApprovalsList"
                :approvalStatus="activeStep === 3 ? headerOperationData.status : null"
                :totalAmountToTransfer="totalAmountToTransfer"
                :totalAmountToSign="totalAmountToSign"
                :showLoader="showLoader"
                :isMobile="getMobileAppState"
                :showFooter="renderStep1 && !isValidated && selectedOperations.length && !showLoader"
                @updateSelectedAccount="updateSelectedAccountHandler"
                @loadMore="loadMoreHandler"
                @selectOperation="saveOperationIdHandler"
                @openDetails="openDetailsHandler"
                @selectAll="selectAllHandler"
                @clearAllSelected="clearAllSelected"
                @selectedStep3Tab="selectedStep3Tab = $event"
                @restart="restartHandler"
                @repeat="repeatHandler"/>
            </div>
        </transition-group>
        <template #empty-state-slot>
            <!-- Empty State -->
            <empty-state-generic
            v-if="!renderStep1 && !showLoader && !applyErrorState"
            illustration="emptyState/approvals"
            :title="t('approvals.pendingSignature.emptyState.title')"
            :description="t('approvals.pendingSignature.emptyState.noOperationsPendingSignature')"/>
        </template>
        <!-- Bottom Total Amounts Displayer -->
        <template #footer-info-displayer>
            <bottom-total-amounts-displayer
            v-if="activeStep === 1"
            :totalNumberOfOperationsToSign="getTotalNumberOfOperationsToSign"
            :numberOfOperationsSelected="selectedOperations.length"
            :totalAmountToTransfer="totalAmountToTransfer"
            :totalAmountToSign="totalAmountToSign"
            :currency="selectedAccount.currency"
            :isMobile="getMobileAppState"/>
        </template>
    </wrapper-features-templates-operations>
    <!-- Generic Centered Modal -->
    <modal-centered-generic
    v-if="showCenteredModal"
    negative
    rightButtonIcon="check-line"
    :title="selectedOperations.length === 1 ? t('approvals.pendingSignature.modal.centeredModal.titleOnlyOneRemoved') : t('approvals.pendingSignature.modal.centeredModal.titleMoreRemoved')"
    :text="selectedOperations.length === 1 ? t('approvals.pendingSignature.modal.centeredModal.textOnlyOneRemoved') : t('approvals.pendingSignature.modal.centeredModal.textMoreRemoved')"
    :leftButtonText="t('approvals.pendingSignature.modal.centeredModal.buttons.no')"
    :rightButtonText="t('approvals.pendingSignature.modal.centeredModal.buttons.yes')"
    @hideMe="showCenteredModal = false"
    @rightButtonClicked="deleteOperationRequest" />
    <!-- Generic Centered Modal: Change Account -->
    <modal-centered-generic
    v-if="showChangeAccountModal"
    :title="t('approvals.pendingSignature.modal.changeAccountModal.title')"
    :text="t('approvals.pendingSignature.modal.changeAccountModal.body')"
    :leftButtonText="t('approvals.pendingSignature.modal.changeAccountModal.leftButton')"
    :rightButtonText="t('approvals.pendingSignature.modal.changeAccountModal.rightButton')"
    @hideMe="showChangeAccountModal = false"
    @rightButtonClicked="forceUpdateAccountHandler"/>
    <!-- Details -->
    <details-base
    v-if="showOperationDetails"
    :operationDetailsInfo="operationDetailsInfo"
    :selectedOperationsIDs="selectedOperationsIDs"
    :showDetailsFooter="activeStep === 1"
    :isMobile="getMobileAppState"
    :headerOperationStatus="headerOperationData"
    :activeStep="activeStep"
    @selectOperation="saveOperationIdHandler"
    @hideMe="showOperationDetails = false"
    @success="successHandler"
    @updateList="updateListHandler"/>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
    <!-- Sticky Buttons Displayer -->
    <sticky-buttons-displayer
    v-if="activeStep === 3"
    :totalNumberOfOperationsToSign="successfulOperationApprovalsList.filter(obj => obj.executed).length"
    :numberOfOperationsSelected="selectedOperations.length"
    :showButtonsDisplayer="selectedOperationsIDs.length > 0"
    :selectedOperationsIDs="selectedOperationsIDs"
    :isMobile="getMobileAppState"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import BottomTotalAmountsDisplayer from '@/components/features/approvals/pendingSignature/BottomTotalAmountsDisplayer'
import DetailsBase from '@/components/features/approvals/details/DetailsBase'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import StickyButtonsDisplayer from '@/components/features/approvals/pendingSignature/StickyButtonsDisplayer'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* Helpers */
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {
    requestAccountsWithOperationsPendingSignature,
    clearAccountsWithOperationsPendingSignature,
    requestOperationApprovalsList,
    clearOperationApprovalsList,
    cancelOperation,
    requestSignMultipleOperations
} = mapActions(moduleMapper.APPROVALS_PENDING_SIGNATURE)
const {
    getAccountsWithOperationsPendingSignature,
    getOperationApprovalsList,
    getApprovalsHasMore,
    getTotalNumberOfOperationsToSign
} = mapGetters(moduleMapper.APPROVALS_PENDING_SIGNATURE)
const { getMobileAppState } = mapGetters()

/* Generic Data */
const scrollValue = ref(null)

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const renderStep1 = ref(false)
const showCenteredModal = ref(false)
const showChangeAccountModal = ref(false)
const confirmChange = ref(false)
const forceSelectedAccountId = ref(0)
const showOperationDetails = ref(false)
const showFeedbackToast = ref(false)

/*************************************************
*                                                *
*                  Steps Logic                   *
*                                                *
*************************************************/
const activeStep = ref(1)
const selectedStep3Tab = ref(1)

const stepperParams = ref([
    { description: t('approvals.pendingSignature.step1.label') },
    { description: t('approvals.pendingSignature.step2.label') }
])

const activeStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/approvals/pendingSignature/Step' + stepNumber))
})

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

const clearStep3 = () => {
    activeStep.value = 1
    isValidated.value = false
    headerOperationData.value = null
    scrollValue.value = 0
    clearAllSelected()
}

/*************************************************
*                                                *
*                  Footer Data                   *
*                                                *
*************************************************/
const buttonsFooterData = computed(() => {
    return [{
        label: t('approvals.pendingSignature.footer.remove', { totalSelected: selectedOperations.value.length }),
        buttonPosition: 'left',
        hidden: activeStep.value !== 1,
        callbackFunction: () => { showCenteredModal.value = true }
    }, {
        label: t('approvals.pendingSignature.footer.confirm'),
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        hidden: activeStep.value !== 1,
        callbackFunction: () => {
            activeStep.value++
            scrollValue.value = 0
        }
    }, {
        label: t('approvals.pendingSignature.footer.edit'),
        icon: 'arrow-left-full',
        hidden: activeStep.value !== 2,
        callbackFunction: () => {
            activeStep.value--
            scrollValue.value = 0
        }
    }, {
        label: t('approvals.pendingSignature.footer.validate'),
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        hidden: activeStep.value !== 2,
        callbackFunction: () => { requestSignOperations() }
    }]
})

const footerButtonsParams = computed(() => {
    return buttonsFooterData.value.filter(ele => !ele.hidden)
})

/*************************************************
*                                                *
*            Selected Account Handler            *
*                                                *
*************************************************/
const selectedAccount = ref({})

const updateSelectedAccountHandler = selectedAccountId => {
    if (selectedAccount.value.id === selectedAccountId) return
	showChangeAccountModal.value = false

	if (!selectedOperations.value.length || confirmChange.value) {
		confirmChange.value = false

		cancelAllPendingRequests()

		selectedAccount.value = getAccountsWithOperationsPendingSignature.value.find(account => account.id === selectedAccountId)
		clearAllSelected()
		resetPaginationParams()
		requestOperationApprovals()
	} else {
		forceSelectedAccountId.value = selectedAccountId
		showChangeAccountModal.value = true
	}
}

const forceUpdateAccountHandler = () => {
	confirmChange.value = true
	updateSelectedAccountHandler(forceSelectedAccountId.value)
}

/*************************************************
*                                                *
*             Select Operations Logic            *
*                                                *
*************************************************/
const selectedOperationsIDs = ref([])
const totalAmountToTransfer = ref(null)
const totalAmountToSign = ref(null)

const selectedOperations = computed(() => {
    if (activeStep.value !== 3) {
        return getOperationApprovalsList.value.filter(operation => selectedOperationsIDs.value.includes(operation.operationId))
    } else {
        return successfulOperationApprovalsList.value.filter(obj => obj.executed).filter(operation => selectedOperationsIDs.value.includes(operation.signedOperationView.operationId))
    }
})

const saveOperationIdHandler = event => {
    if (!selectedOperationsIDs.value.includes(event)) {
        selectedOperationsIDs.value.push(event)
    } else {
        const idToRemove = selectedOperationsIDs.value.findIndex(id => id === event)

        selectedOperationsIDs.value.splice(idToRemove, 1)
    }

    sumTotalAmounts()
}

/** Sum total amounts to show on footer displayer and on step 2 */
const sumTotalAmounts = () => {
    totalAmountToTransfer.value = selectedOperations.value.reduce((acc, obj) => {
        if (obj.amount && obj.amount.amount && obj.dependentOnMe) acc += obj.amount.amount
        return acc
    }, 0)

    totalAmountToSign.value = selectedOperations.value.reduce((acc, obj) => {
        if (obj.amount && obj.amount.amount && !obj.dependentOnMe) acc += obj.amount.amount
        return acc
    }, 0)
}

const clearAllSelected = () => {
    selectedOperationsIDs.value = []
}

/** Step 3 specific */
const selectAllHandler = operationsIds => {
    clearAllSelected()
    selectedOperationsIDs.value = [...operationsIds]
}

const repeatHandler = () => {
    clearStep3()
    selectedOperationsIDs.value = failedOperationApprovalsList.value.map(operation => operation.signedOperationView.operationId)
    requestOperationApprovals()
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const tableLoadingMore = ref(false)
const paginationParams = ref({})

const resetPaginationParams = () => {
    paginationParams.value = {
        pageNumber: 1
    }
}

const initialRequest = async () => {
    cancelAllPendingRequests()
    resetPaginationParams()

    showLoader.value = true
    applyErrorState.value = false
    renderStep1.value = false

    try {
        await requestAccountsWithOperationsPendingSignature()

        if (getAccountsWithOperationsPendingSignature.value.length) {
            /* Setting Initial Selected Account */
            selectedAccount.value = getAccountsWithOperationsPendingSignature.value[0]

            renderStep1.value = true

            requestOperationApprovals()
        } else {
            showLoader.value = false
        }
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequest)
    }
}

const requestOperationApprovals = async () => {
    paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true
    applyErrorState.value = false

    try {
        await requestOperationApprovalsList({
            ...paginationParams.value,
            status: 'NotSignedByMe',
            accountIds: selectedAccount.value.id
        })
        showLoader.value = false
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, requestOperationApprovals)
    }
}

const deleteOperationRequest = async () => {
    showOverlayLoader.value = true
    showCenteredModal.value = false

    try {
        const res = await cancelOperation({ id: [...selectedOperationsIDs.value] })
        showOverlayLoader.value = false

        successHandler(res.data)
        updateListHandler()
    } catch (error) {
        showOverlayLoader.value = false
		errorHandler(error.response.data, deleteOperationRequest)
    }
}

const requestSignOperations = async () => {
    showOverlayLoader.value = true

    try {
        await requestSignMultipleOperations({ operationIds: [...selectedOperationsIDs.value] })

        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestSignOperations)
    }
}

const loadMoreHandler = () => {
    paginationParams.value.pageNumber++
    requestOperationApprovals()
}

const updateListHandler = () => {
    showOperationDetails.value = false
    clearAllSelected()
    resetPaginationParams()
    requestOperationApprovals()
}

/** Step 3 specific */
const restartHandler = () => {
    clearStep3()
    initialRequest()
}

/*************************************************
*                                                *
*            Operations Details Modal            *
*                                                *
*************************************************/
const operationDetailsInfo = ref({})

const filterSignatureList = (operation) => {
    if (operation.status.id === 3) {
        return operation.signatureList.filter(signature => signature.weight && signature.signDate).map(obj => ({ ...obj }))
    } else {
        return operation.signatureList.filter(signature => signature.weight).map(obj => ({ ...obj }))
    }
}

const openDetailsHandler = operation => {
    cancelAllPendingRequests()
    tableLoadingMore.value = false
    showOperationDetails.value = true
    operationDetailsInfo.value = {
        ...operation,
        status: operation.status.classes === 'positive' && (activeStep.value !== 1 || activeStep.value !== 2) ? operation.status : { id: operation.status.id },
        signatureList: filterSignatureList(operation)
    }
}

/*************************************************
*                                                *
*             Challenge Success Handler          *
*                                                *
*************************************************/
const isValidated = ref(false)
const successfulOperationApprovalsList = ref([])
const failedOperationApprovalsList = ref([])
const headerOperationData = ref(null)

const challengeSuccessHandler = data => {
    if (data.errorLevel === 'ERROR') return

    isValidated.value = true

    clearAllSelected()

    successfulOperationApprovalsList.value = data.data.successfulList
    failedOperationApprovalsList.value = data.data.failedList
    let parsedStatus = data.errorLevel
    if (data.errorLevel === 'INFO') {
        if (failedOperationApprovalsList.value.length > 0 && successfulOperationApprovalsList.value.length > 0) parsedStatus = 'WARNING'
        else if (failedOperationApprovalsList.value.length > 0) parsedStatus = 'ERROR'
    }

    let parsedMessage = data.message

    if (data.errorLevel === 'INFO') {
        if (failedOperationApprovalsList.value.length > 0 && successfulOperationApprovalsList.value.length > 0) {
            parsedMessage = t('approvals.pendingSignature.step3.warningTitleHeaderOperation')
        } else if (failedOperationApprovalsList.value.length > 0) parsedMessage = t('approvals.pendingSignature.step3.errorTitleHeaderOperation')
    }

    headerOperationData.value = {
        status: parsedStatus,
        title: parsedMessage
    }

    activeStep.value = 3
    scrollValue.value = 0
}

/*************************************************
*                                                *
*              Error/Success Handler             *
*                                                *
*************************************************/
const applyErrorState = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
	feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

initialRequest()

onBeforeUnmount(() => {
    clearAccountsWithOperationsPendingSignature()
    clearOperationApprovalsList()
})
</script>

<style lang="scss" scoped>

.content-wrapper {
    @include commonEnteringAnimation;

    width: 100%;

    &.in-step-3 {
        padding-top: 24px;
    }
}
</style>
